/** One choice: the value the caller gets back, and the text the reader sees. */
export type ViewerSelectOption = [value: string, text: string];

export interface ViewerSelectOptions {
  label: string;
  options: Array<ViewerSelectOption>;
  onChange: (value: string) => void;
}

export interface ViewerSelect {
  element: HTMLElement;
  focus(): void;
  readonly value: string;
}

/** How long a letter keeps looking for the next word starting with it. */
const TYPE_AHEAD_MS = 700;

const PART_ATTRIBUTE = 'part';
const EXPANDED_ATTRIBUTE = 'aria-expanded';

let selectId = 0;

/**
 * A combobox that only selects, with its listbox in the document rather than in the browser chrome —
 * so the host theme can style it like everything else.
 *
 * @param {Document} document The document to build in.
 * @param {ViewerSelectOptions} settings Label, choices, and where the choice goes.
 * @return {ViewerSelect} The element to place, and the value it holds.
 */
export function createViewerSelect(document: Document, settings: ViewerSelectOptions): ViewerSelect {
  const { label, options, onChange } = settings;
  const root = document.createElement('div');
  const button = document.createElement('button');
  const caption = document.createElement('span');
  const arrow = document.createElement('span');
  const list = document.createElement('div');

  root.className = 'viewer-select';

  button.type = 'button';
  button.className = 'viewer-select-trigger';
  button.setAttribute(PART_ATTRIBUTE, 'viewer-dialog-select');
  button.setAttribute('role', 'combobox');
  button.setAttribute('aria-label', label);
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute(EXPANDED_ATTRIBUTE, 'false');

  caption.textContent = 'Choose a format';
  arrow.className = 'viewer-select-arrow';
  arrow.setAttribute('aria-hidden', 'true');
  button.append(caption, arrow);

  list.id = `viewer-select-${++selectId}`;
  list.className = 'viewer-select-options';
  list.setAttribute(PART_ATTRIBUTE, 'viewer-dialog-options');
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', label);
  list.hidden = true;
  button.setAttribute('aria-controls', list.id);

  let value = '';
  let active = 0;
  let prefix = '';
  let lastTyped = 0;

  const rows = options.map(([, text], index) => {
    const option = document.createElement('div');

    option.id = `${list.id}-${index}`;
    option.className = 'viewer-select-option';
    option.setAttribute(PART_ATTRIBUTE, 'viewer-dialog-option');
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    option.textContent = text;
    // The pointer must not take focus off the button, or the list closes before the click lands.
    option.onpointerdown = (event: PointerEvent): void => event.preventDefault();
    option.onpointermove = (): void => highlight(index);
    option.onclick = (): void => commit(index);
    list.append(option);

    return option;
  });

  root.append(button, list);

  function highlight(index: number): void {
    active = Math.max(0, Math.min(rows.length - 1, index));
    rows.forEach((row, position) => row.classList.toggle('active', position === active));
    button.setAttribute('aria-activedescendant', rows[active].id);
    rows[active].scrollIntoView({ block: 'nearest' });
  }

  function close(): void {
    list.hidden = true;
    button.setAttribute(EXPANDED_ATTRIBUTE, 'false');
    button.removeAttribute('aria-activedescendant');
    prefix = '';
  }

  function show(): void {
    list.hidden = false;
    button.setAttribute(EXPANDED_ATTRIBUTE, 'true');
    highlight(
      Math.max(
        0,
        options.findIndex(([key]) => key === value)
      )
    );
  }

  function commit(index: number): void {
    value = options[index][0];
    caption.textContent = options[index][1];
    rows.forEach((row, position) => row.setAttribute('aria-selected', String(position === index)));
    close();
    button.focus();
    onChange(value);
  }

  function moveTo(key: string): void {
    if (key === 'Home') return highlight(0);

    if (key === 'End') return highlight(rows.length - 1);

    highlight(active + (key === 'ArrowDown' ? 1 : -1));
  }

  function typeAhead(key: string): void {
    const now = Date.now();

    prefix = now - lastTyped < TYPE_AHEAD_MS ? prefix + key.toLowerCase() : key.toLowerCase();
    lastTyped = now;

    if (list.hidden) show();

    const index = options.findIndex(([, text]) => text.toLowerCase().startsWith(prefix));

    if (index >= 0) highlight(index);
  }

  button.onclick = (): void => (list.hidden ? show() : close());
  button.onblur = close;
  button.onkeydown = (event: KeyboardEvent): void => {
    const key = event.key;

    if (key === 'Escape' && !list.hidden) {
      event.preventDefault();
      // The dialog around it must not close as well: this key belongs to the open list.
      event.stopPropagation();
      close();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      event.preventDefault();

      if (!list.hidden) moveTo(key);
      else {
        show();

        if (key === 'End') highlight(rows.length - 1);
      }
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();

      if (list.hidden) show();
      else commit(active);
    } else if (key === 'Tab') close();
    else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeAhead(key);
    }
  };

  return {
    element: root,
    focus: (): void => button.focus(),
    get value(): string {
      return value;
    }
  };
}
